import { create } from 'zustand';
import { apiClient } from '@/lib/api';
import type { AdminOverviewResponse, AdminGameStatusResponse, ErrorEntry } from '@/lib/api';

export type HealthEnv = 'local' | 'staging';

const IS_STAGING = import.meta.env.VITE_ENV === 'staging' || import.meta.env.PROD;
export const DEFAULT_HEALTH_ENV: HealthEnv = IS_STAGING ? 'staging' : 'local';
export const SHOW_ENV_TOGGLE = !IS_STAGING;

export interface ServiceStatus {
  name: string;
  status: string;
  url?: string;
}

interface AdminState {
  overview: AdminOverviewResponse | null;
  gameStatus: AdminGameStatusResponse | null;
  lobbyErrors: ErrorEntry[];
  services: ServiceStatus[];
  healthEnv: HealthEnv;
  loading: boolean;
  error: string | null;
  lastFetchedAt: number | null;

  fetch: () => Promise<void>;
  setHealthEnv: (env: HealthEnv) => void;
  startAutoRefresh: () => () => void;
  reset: () => void;
}

const REFRESH_INTERVAL_MS = 30_000;

export const useAdminStore = create<AdminState>()((set, get) => ({
  overview: null,
  gameStatus: null,
  lobbyErrors: [],
  services: [],
  healthEnv: DEFAULT_HEALTH_ENV,
  loading: true,
  error: null,
  lastFetchedAt: null,

  fetch: async () => {
    set({ loading: !get().overview, error: null });

    const [overviewResult, gameStatusResult, healthResult, lobbyErrorsResult] =
      await Promise.allSettled([
        apiClient.getAdminOverview(),
        apiClient.getAdminGameStatus(),
        apiClient.getServiceHealth(get().healthEnv),
        apiClient.getAdminLobbyErrors(),
      ]);

    const overview = overviewResult.status === 'fulfilled' ? overviewResult.value : get().overview;
    const gameStatus =
      gameStatusResult.status === 'fulfilled' ? gameStatusResult.value : get().gameStatus;
    const services = healthResult.status === 'fulfilled' ? healthResult.value : get().services;
    const lobbyErrors =
      lobbyErrorsResult.status === 'fulfilled' ? lobbyErrorsResult.value : get().lobbyErrors;

    const allFailed =
      overviewResult.status === 'rejected' &&
      gameStatusResult.status === 'rejected' &&
      healthResult.status === 'rejected';

    set({
      overview,
      gameStatus,
      lobbyErrors,
      services,
      loading: false,
      lastFetchedAt: Date.now(),
      error: allFailed ? 'Failed to fetch admin data. Services may be down.' : null,
    });
  },

  setHealthEnv: (env: HealthEnv) => {
    set({ healthEnv: env, services: [] });
    get().fetch();
  },

  startAutoRefresh: () => {
    get().fetch();
    const id = setInterval(() => get().fetch(), REFRESH_INTERVAL_MS);
    return () => clearInterval(id);
  },

  reset: () =>
    set({
      overview: null,
      gameStatus: null,
      lobbyErrors: [],
      services: [],
      healthEnv: DEFAULT_HEALTH_ENV,
      loading: true,
      error: null,
      lastFetchedAt: null,
    }),
}));
